import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  LayoutDashboard,
  Wallet,
  FileText,
  CircleDollarSign,
  FolderOpen,
  Shield,
  Receipt,
  Bell,
  Briefcase,
  BarChart3,
  Settings,
  HelpCircle,
  LogOut,
  TreePine,
  Users,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Upload,
  ArrowDownCircle,
  ArrowUpCircle,
  ShieldCheck,
  TrendingUp,
  ArrowLeftRight,
  BookOpen
} from 'lucide-react';

const investorNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/portfolio', icon: Wallet, label: 'Investment Portfolio' },
  { to: '/ledger', icon: BookOpen, label: 'Ledger' },
  { to: '/marketplace', icon: ArrowLeftRight, label: 'Marketplace' },
  { to: '/withdrawals', icon: ArrowDownCircle, label: 'Withdrawals' },
  { to: '/additions', icon: ArrowUpCircle, label: 'Additions' },
  { to: '/statements', icon: FileText, label: 'Statements' },
  { to: '/distributions', icon: CircleDollarSign, label: 'Distributions' },
  { to: '/documents', icon: FolderOpen, label: 'Document Vault' },
  { to: '/kyc', icon: Shield, label: 'KYC & FICA' },
  { to: '/tax', icon: Receipt, label: 'Tax Centre' },
  { to: '/notices', icon: Bell, label: 'Notices' },
  { to: '/deals', icon: Briefcase, label: 'Deal Room' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
];

const complianceNav = [
  { to: '/compliance', icon: ShieldCheck, label: 'KYC Compliance' },
];

const irNav = [
  { to: '/ir', icon: TrendingUp, label: 'Portfolio (IR)' },
];

const adminItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Admin Dashboard', highlight: true },
  { to: '/admin/investors', icon: Users, label: 'Manage Investors' },
  { to: '/admin/statements', icon: Upload, label: 'Statements' },
  { to: '/admin/kyc-review', icon: Shield, label: 'KYC Review' },
  { to: '/admin/notices', icon: Bell, label: 'Manage Notices' },
  { to: '/admin/deals', icon: Briefcase, label: 'Manage Deals' },
];

const bottomItems = [
  { to: '/profile', icon: Users, label: 'Profile' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/support', icon: HelpCircle, label: 'Help & Support' },
];

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const { user, logout, isAdmin, isCompliance, isIR } = useAuth();
  const navigate = useNavigate();

  const navItems = isCompliance
    ? complianceNav
    : isIR
    ? irNav
    : investorNav;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
          isActive
            ? 'bg-white/10 text-white'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        } ${collapsed ? 'justify-center' : ''}`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${collapsed ? '' : ''}`} />
      {!collapsed && <span className="text-sm font-medium">{label}</span>}
    </NavLink>
  );

  return (
    <>
      {/* Backdrop — mobile/tablet only, closes the drawer on tap-away */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-full bg-[#1D2D2A] text-white z-50 transition-all duration-300 w-[260px] ${
          collapsed ? 'lg:w-[70px]' : 'lg:w-[260px]'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="sidebar"
      >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={`flex items-center h-16 px-4 border-b border-white/10 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <img 
            src="/logo-tree.svg" 
            alt="Plane Tree Capital" 
            className="h-8 w-8 flex-shrink-0 brightness-0 invert"
          />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-bold text-lg font-['Manrope'] leading-tight">Plane Tree</span>
              <span className="text-xs text-white/70">Capital</span>
            </div>
          )}
        </div>

        {/* Toggle Button — icon-rail collapse is a desktop affordance only;
            on mobile/tablet the drawer is either open (full width) or
            closed (off-canvas), so this button is hidden below lg. */}
        <button
          onClick={onToggle}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-[#1D2D2A] border border-white/20 rounded-full items-center justify-center text-white/70 hover:text-white hover:bg-[#243D37] transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>

        {/* Navigation — clicking any nav link closes the mobile drawer */}
        <ScrollArea
          className="flex-1 px-3 py-4"
          onClick={(e) => { if (e.target.closest('a')) onMobileClose?.(); }}
        >
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          {/* Admin Section */}
          {isAdmin && (
            <>
              <Separator className="my-4 bg-white/10" />
              {!collapsed && (
                <p className="px-3 text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                  Admin
                </p>
              )}
              <nav className="space-y-1">
                {adminItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </nav>
            </>
          )}

          <Separator className="my-4 bg-white/10" />

          {/* Bottom Items */}
          <nav className="space-y-1">
            {bottomItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          {!collapsed ? (
            <div className="flex items-center gap-3 mb-3 px-3">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-white/60 truncate">{user?.email}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-3">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full text-white/70 hover:text-white hover:bg-white/10 ${
              collapsed ? 'px-0 justify-center' : 'justify-start'
            }`}
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span className="ml-3">Sign Out</span>}
          </Button>
        </div>
      </div>
      </aside>
    </>
  );
};

export default Sidebar;
